import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { unwrappedComparisonTeamNameAtom } from '@/atoms/queries/team/teamComparison';
import { unwrappedTeamRenderedComparisonMinutesPlayedAtom } from '@/atoms/queries/team/teamLeagueAverageRadar';
import { unwrappedTeamAggsMinutesPlayedAtom } from '@/atoms/queries/team/teamRadar';
import { selectedTeamNameAtom } from '@/atoms/team/info';
import { BioDataItem, BioTableContent } from '@/components/radar/table/RadarBioTable';
import { assertBioRowCount, useFormatMinutesPlayed } from '@/hooks/radar/radarBioUtils';
import { useTeamCompetitionAverageBioColumn } from '@/hooks/radar/useTeamCompetitionAverageBioColumn';
import {
  useComparisonTeamFilterDisplayOptions,
  useTeamFilterDisplayOptions,
} from '@/hooks/radar/useTeamFilterDisplayOptions';
import { useIsCompetitionAverageMode } from '@/hooks/useIsCompetitionAverageMode';
import { useComparisonTeamGameSummary, useTeamGameSummary } from '@/hooks/useTeamGameSummary';

// The number of bio rows. Must be updated whenever rows are added or removed from the hook.
export const MAX_TEAM_BIO_ROW_COUNT = 4;

export const useTeamBioData = () => {
  const { t } = useTranslation(['filters', 'entity']);
  const formatMinutesPlayed = useFormatMinutesPlayed();
  const isCompetitionAverageMode = useIsCompetitionAverageMode();

  // Entity info
  const entityName = useAtomValue(selectedTeamNameAtom);
  const comparisonEntityName = useAtomValue(unwrappedComparisonTeamNameAtom);

  // Minutes — mode-resolved so the comparison column shows league-average minutes in competition-average mode
  const minutesPlayed = useAtomValue(unwrappedTeamAggsMinutesPlayedAtom);
  const comparisonMinutesPlayed = useAtomValue(unwrappedTeamRenderedComparisonMinutesPlayedAtom);

  // Competitions / Seasons
  const { competitionsToDisplay, seasonsToDisplay } = useTeamFilterDisplayOptions();
  const { competitionsToDisplay: comparisonCompetitionsToDisplay, seasonsToDisplay: comparisonSeasonsToDisplay } =
    useComparisonTeamFilterDisplayOptions();

  // Games
  const gameSummary = useTeamGameSummary();
  const comparisonGameSummary = useComparisonTeamGameSummary();

  // Bio values
  const minutesValue = formatMinutesPlayed(minutesPlayed);
  const comparisonMinutesValue = formatMinutesPlayed(comparisonMinutesPlayed);

  // The comparison column diverges by mode: an entity comparison shows the other team's scope, while
  // a competition average shows the selected competition/season.
  const competitionAverageColumn = useTeamCompetitionAverageBioColumn();
  const entityComparisonColumn = {
    name: comparisonEntityName,
    competition: <BioTableContent options={comparisonCompetitionsToDisplay} />,
    season: <BioTableContent options={comparisonSeasonsToDisplay} />,
    game: comparisonGameSummary,
  };
  const comparison = isCompetitionAverageMode ? competitionAverageColumn : entityComparisonColumn;

  const bioData: BioDataItem[] = [
    {
      label: t('summary.minutesPlayed', { ns: 'filters' }),
      value: minutesValue,
      comparisonValue: comparisonMinutesValue,
    },
    {
      label: t('competition_one', { ns: 'entity' }),
      value: <BioTableContent options={competitionsToDisplay} />,
      comparisonValue: comparison.competition,
    },
    {
      label: t('season_one', { ns: 'entity' }),
      value: <BioTableContent options={seasonsToDisplay} />,
      comparisonValue: comparison.season,
    },
    {
      label: t('game_one', { ns: 'entity' }),
      value: gameSummary,
      comparisonValue: comparison.game,
    },
  ];

  assertBioRowCount(bioData, MAX_TEAM_BIO_ROW_COUNT);

  return { bioData, entityName, comparisonEntityName: comparison.name };
};
